import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export {
  getInitials,
  formatDuration,
  formatShortDate,
  formatDateTime,
  formatDateTimeInZone,
  formatDateTimeWithSecondsInZone,
  viewerTimeZoneLabel,
  timeZoneOffsetLabel,
  formatHumanDuration,
  formatFileSize,
  formatRelativeTime,
  formatWeekRange,
  formatMonthLabel,
} from '@doneverse/data/utils'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Href for a user-entered link. Bare domains (e.g. "www.x.com") would
 * otherwise resolve as a relative dashboard path — prepend https://.
 */
export function externalHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

/**
 * A user-entered link as an absolute http(s) URL, or null if it isn't one.
 *
 * The API stores links as absolute URLs, so what someone types ("acme.com",
 * "figma.com/file/abc") has to be completed before it is sent. A hostname
 * without a dot is rejected: `new URL('https://notaurl')` parses happily, and
 * saving that would give a row that opens nowhere.
 */
export function normalizeExternalUrl(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  try {
    const parsed = new URL(externalHref(trimmed))
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
    if (!parsed.hostname.includes('.')) return null
    return parsed.toString()
  } catch {
    return null
  }
}

/** Display form for a link — the host, with "www." dropped. */
export function linkHost(url: string): string {
  try {
    return new URL(externalHref(url)).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}
